import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Building2, Truck, Wrench, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const inputCls = "border border-white/10 bg-[#0D1017] px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-amber-600/60";
const btnCls = "inline-flex items-center gap-2 bg-amber-600 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-500 disabled:opacity-60";
const headCls = "flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400";
const ACCOUNT_EMPTY = { business_name: "", name: "", email: "", password: "" };
const VEHICLE_EMPTY = { rego: "", make: "", model: "", year: "" };
const RECORD_EMPTY = { service_date: "", odometer_km: "", notes: "" };

const useSubmit = (fn, onDone) => {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await fn(); onDone?.(); } catch (err) { toast.error(formatApiError(err, "Could not save")); } finally { setBusy(false); }
  };
  return [run, busy];
};

const AccountsPanel = ({ accounts, selected, onSelect, reload }) => {
  const [form, setForm] = useState(ACCOUNT_EMPTY);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const [submit, busy] = useSubmit(async () => { await api.post("/admin/fleet/accounts", form); toast.success("Fleet customer created"); }, () => { setForm(ACCOUNT_EMPTY); reload(); });
  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.business_name} and all its vehicles and records?`)) return;
    await api.delete(`/admin/fleet/accounts/${a.id}`); toast.success("Customer deleted"); reload();
  };
  const resetPw = async (a) => {
    const pw = window.prompt(`New password for ${a.business_name} (min 8 characters):`);
    if (!pw) return;
    try { await api.patch(`/admin/fleet/accounts/${a.id}/password`, { password: pw }); toast.success("Password updated"); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="border border-white/10 bg-[#12151C] p-5" data-testid="fleet-accounts-panel">
      <h3 className={headCls}><Building2 size={13} className="text-amber-500" /> Fleet customers</h3>
      <form onSubmit={submit} className="mt-4 grid grid-cols-1 gap-3" data-testid="fleet-account-form">
        <input required value={form.business_name} onChange={set("business_name")} placeholder="Business name *" className={inputCls} data-testid="fleet-account-business-input" />
        <input required value={form.name} onChange={set("name")} placeholder="Contact name *" className={inputCls} data-testid="fleet-account-name-input" />
        <input required type="email" value={form.email} onChange={set("email")} placeholder="Login email *" className={inputCls} data-testid="fleet-account-email-input" />
        <input required minLength={8} type="text" autoComplete="off" value={form.password} onChange={set("password")} placeholder="Password (min 8) *" className={inputCls} data-testid="fleet-account-password-input" />
        <button disabled={busy} className={btnCls} data-testid="fleet-account-submit">{busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create customer</button>
      </form>
      <div className="mt-5 space-y-2" data-testid="fleet-accounts-list">
        {accounts.length === 0 && <p className="font-mono text-[11px] text-slate-600">No fleet customers yet.</p>}
        {accounts.map((a) => (
          <div key={a.id} className={`flex items-center justify-between gap-2 border px-3 py-2.5 ${selected?.id === a.id ? "border-amber-600/60 bg-amber-600/5" : "border-white/10"}`} data-testid={`fleet-account-${a.id}`}>
            <button onClick={() => onSelect(a)} className="flex-1 text-left" data-testid={`fleet-account-select-${a.id}`}>
              <div className="text-sm font-semibold text-slate-100">{a.business_name}</div>
              <div className="font-mono text-[10px] text-slate-500">{a.name} · {a.email} · {a.vehicle_count} vehicle{a.vehicle_count === 1 ? "" : "s"}</div>
            </button>
            <button onClick={() => resetPw(a)} title="Reset password" className="p-1.5 text-slate-500 hover:text-amber-500" data-testid={`fleet-account-password-${a.id}`}><KeyRound size={13} /></button>
            <button onClick={() => remove(a)} title="Delete" className="p-1.5 text-slate-600 hover:text-red-400" data-testid={`fleet-account-delete-${a.id}`}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const VehiclesPanel = ({ account, vehicles, selected, onSelect, reload }) => {
  const [form, setForm] = useState(VEHICLE_EMPTY);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const [submit, busy] = useSubmit(async () => {
    await api.post(`/admin/fleet/accounts/${account.id}/vehicles`, { ...form, year: form.year ? Number(form.year) : undefined });
    toast.success("Vehicle added");
  }, () => { setForm(VEHICLE_EMPTY); reload(); });
  const remove = async (v) => {
    if (!window.confirm(`Remove ${v.rego} and its records?`)) return;
    await api.delete(`/admin/fleet/vehicles/${v.id}`); toast.success("Vehicle removed"); reload();
  };
  return (
    <div className="border border-white/10 bg-[#12151C] p-5" data-testid="fleet-vehicles-panel">
      <h3 className={headCls}><Truck size={13} className="text-amber-500" /> Vehicles — {account.business_name}</h3>
      <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-3" data-testid="fleet-vehicle-form">
        <input required value={form.rego} onChange={set("rego")} placeholder="Rego *" className={`${inputCls} font-mono uppercase`} data-testid="fleet-vehicle-rego-input" />
        <input value={form.year} onChange={set("year")} type="number" min="1950" max="2100" placeholder="Year" className={inputCls} data-testid="fleet-vehicle-year-input" />
        <input required value={form.make} onChange={set("make")} placeholder="Make *" className={inputCls} data-testid="fleet-vehicle-make-input" />
        <input required value={form.model} onChange={set("model")} placeholder="Model *" className={inputCls} data-testid="fleet-vehicle-model-input" />
        <button disabled={busy} className={`${btnCls} col-span-2`} data-testid="fleet-vehicle-submit">{busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add vehicle</button>
      </form>
      <div className="mt-5 space-y-2" data-testid="fleet-vehicles-list">
        {vehicles.length === 0 && <p className="font-mono text-[11px] text-slate-600">No vehicles assigned yet.</p>}
        {vehicles.map((v) => (
          <div key={v.id} className={`flex items-center justify-between gap-2 border px-3 py-2.5 ${selected?.id === v.id ? "border-amber-600/60 bg-amber-600/5" : "border-white/10"}`} data-testid={`fleet-vehicle-row-${v.rego}`}>
            <button onClick={() => onSelect(v)} className="flex flex-1 items-center gap-3 text-left" data-testid={`fleet-vehicle-select-${v.rego}`}>
              <span className="border border-teal-500/40 bg-teal-500/10 px-2 py-0.5 font-mono text-[11px] tracking-[0.15em] text-teal-400">{v.rego}</span>
              <span className="text-sm text-slate-200">{[v.year, v.make, v.model].filter(Boolean).join(" ")}</span>
            </button>
            <button onClick={() => remove(v)} className="p-1.5 text-slate-600 hover:text-red-400" data-testid={`fleet-vehicle-delete-${v.rego}`}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const RecordsPanel = ({ vehicle, records, reload }) => {
  const [form, setForm] = useState(RECORD_EMPTY);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const [submit, busy] = useSubmit(async () => {
    await api.post(`/admin/fleet/vehicles/${vehicle.id}/records`, { service_date: form.service_date, notes: form.notes, odometer_km: form.odometer_km ? Number(form.odometer_km) : undefined });
    toast.success("Service record added");
  }, () => { setForm(RECORD_EMPTY); reload(); });
  const remove = async (r) => { await api.delete(`/admin/fleet/records/${r.id}`); toast.success("Record deleted"); reload(); };
  return (
    <div className="border border-white/10 bg-[#12151C] p-5" data-testid="fleet-records-panel">
      <h3 className={headCls}><Wrench size={13} className="text-amber-500" /> Service records — {vehicle.rego}</h3>
      <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-3" data-testid="fleet-record-form">
        <input required type="date" value={form.service_date} onChange={set("service_date")} className={inputCls} data-testid="fleet-record-date-input" />
        <input type="number" min="0" value={form.odometer_km} onChange={set("odometer_km")} placeholder="Odometer km (optional)" className={inputCls} data-testid="fleet-record-odometer-input" />
        <textarea required rows={2} value={form.notes} onChange={set("notes")} placeholder="Work notes *" className={`${inputCls} col-span-2 resize-none`} data-testid="fleet-record-notes-input" />
        <button disabled={busy} className={`${btnCls} col-span-2`} data-testid="fleet-record-submit">{busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add record</button>
      </form>
      <div className="mt-5 space-y-2" data-testid="fleet-records-list">
        {records.length === 0 && <p className="font-mono text-[11px] text-slate-600">No records yet.</p>}
        {records.map((r) => (
          <div key={r.id} className="flex items-start justify-between gap-3 border border-white/10 px-3 py-2.5" data-testid={`fleet-record-row-${r.id}`}>
            <div>
              <div className="font-mono text-[11px] text-slate-400">{r.service_date}{r.odometer_km != null ? ` · ${r.odometer_km.toLocaleString()} km` : ""}</div>
              <div className="mt-1 text-sm text-slate-200">{r.notes}</div>
            </div>
            <button onClick={() => remove(r)} className="p-1.5 text-slate-600 hover:text-red-400" data-testid={`fleet-record-delete-${r.id}`}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FleetAdmin = () => {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicle, setVehicle] = useState(null);
  const [records, setRecords] = useState([]);

  const loadAccounts = useCallback(async () => { const { data } = await api.get("/admin/fleet/accounts"); setAccounts(data); }, []);
  const loadVehicles = useCallback(async () => { if (!account) return; const { data } = await api.get(`/admin/fleet/accounts/${account.id}/vehicles`); setVehicles(data); loadAccounts(); }, [account, loadAccounts]);
  const loadRecords = useCallback(async () => { if (!vehicle) return; const { data } = await api.get(`/admin/fleet/vehicles/${vehicle.id}/records`); setRecords(data); }, [vehicle]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { setVehicle(null); setVehicles([]); loadVehicles(); }, [account, loadVehicles]);
  useEffect(() => { setRecords([]); loadRecords(); }, [vehicle, loadRecords]);

  return (
    <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-3" data-testid="fleet-admin">
      <AccountsPanel accounts={accounts} selected={account} onSelect={setAccount} reload={() => { loadAccounts(); if (account && !accounts.find((a) => a.id === account.id)) setAccount(null); }} />
      {account ? <VehiclesPanel account={account} vehicles={vehicles} selected={vehicle} onSelect={setVehicle} reload={loadVehicles} /> : <div className="border border-dashed border-white/10 p-5 font-mono text-[11px] text-slate-600" data-testid="fleet-select-account-hint">Select a customer to manage vehicles.</div>}
      {vehicle ? <RecordsPanel vehicle={vehicle} records={records} reload={loadRecords} /> : <div className="border border-dashed border-white/10 p-5 font-mono text-[11px] text-slate-600" data-testid="fleet-select-vehicle-hint">Select a vehicle to add service records.</div>}
    </div>
  );
};
